import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { quizAPI, groupAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import CreateQuiz from '../components/CreateQuiz';
import FlaggedQuestions from '../components/FlaggedQuestions';
import IssuesManagement from '../components/IssuesManagement';
import { FormattedText } from '../utils/formatText';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import ReportIssueButton from '../components/ReportIssueButton';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

function AdminQuizzes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Verify user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [viewingQuestions, setViewingQuestions] = useState(null);
  const [viewingFlaggedQuestions, setViewingFlaggedQuestions] = useState(false);
  const [viewingIssues, setViewingIssues] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [editingOption, setEditingOption] = useState(null); // { questionId, optionIndex }
  const [newOptionText, setNewOptionText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allGroups, setAllGroups] = useState([]);
  const [allTags, setAllTags] = useState([]);
  
  // Get current page from URL or default to 1
  const currentPage = parseInt(searchParams.get('page')) || 1;
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalQuizzes: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const fetchQuizzes = useCallback(async (page = 1, filters = {}) => {
    setFetching(true);
    try {
      const response = await quizAPI.getMyQuizzes(page, 10, filters);
      setQuizzes(response.data.quizzes);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      if (error.response?.status === 403) {
        showToast('Access denied. Admin only.', 'error');
        navigate('/user');
      }
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [navigate, showToast]);

  // Check if a quiz was passed via location state (from group details or student performance)
  useEffect(() => {
    if (location.state?.selectedQuiz) {
      const passedQuiz = location.state.selectedQuiz;
      
      // Navigate to quiz details page instead of showing on same page
      navigate(`/admin/quiz/${passedQuiz._id}`, {
        state: {
          quiz: passedQuiz,
          filterStudentId: location.state?.filterStudentId
        },
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const filters = {
      search: searchQuery,
      group: selectedGroup,
      tags: selectedTags,
      startDate,
      endDate
    };
    fetchQuizzes(currentPage, filters);
  }, [currentPage, searchQuery, selectedGroup, selectedTags, startDate, endDate, fetchQuizzes]);

  // Fetch all groups and tags on mount
  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [groupsRes, tagsRes] = await Promise.all([
          groupAPI.getMyGroups(),
          quizAPI.getQuizList()
        ]);
        setAllGroups(groupsRes.data.groups || []);
        
        // Extract unique tags and groups from all quizzes
        const tagsSet = new Set();
        const groupsSet = new Set();
        if (tagsRes.data && Array.isArray(tagsRes.data)) {
          tagsRes.data.forEach(quiz => {
            if (quiz.tags) {
              quiz.tags.forEach(tag => {
                if (tag && tag._id) {
                  tagsSet.add(JSON.stringify({ _id: tag._id, name: tag.name }));
                }
              });
            }
            if (quiz.group && quiz.group._id) {
              groupsSet.add(JSON.stringify({ _id: quiz.group._id, name: quiz.group.name }));
            }
          });
        }
        setAllTags(Array.from(tagsSet).map(t => JSON.parse(t)));
        
        // Merge groups from both sources
        const mergedGroups = [...(groupsRes.data.groups || [])];
        const existingGroupIds = new Set(mergedGroups.map(g => g._id));
        Array.from(groupsSet).forEach(gStr => {
          const g = JSON.parse(gStr);
          if (!existingGroupIds.has(g._id)) {
            mergedGroups.push(g);
          }
        });
        setAllGroups(mergedGroups);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchFiltersData();
  }, []);

  // Close tags dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('tags-dropdown');
      const trigger = event.target.closest('[data-tags-trigger]');
      if (dropdown && dropdown.style.display === 'block' && !trigger && !dropdown.contains(event.target)) {
        dropdown.style.display = 'none';
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleQuizCreated = () => {
    setShowCreateForm(false);
    setSearchParams({ page: '1' }); // Reset to first page
    const filters = { search: searchQuery, group: selectedGroup, tags: selectedTags, startDate, endDate };
    fetchQuizzes(1, filters);
  };

  const handleUpdateCorrectAnswer = async (questionId, questionIndex) => {
    if (newCorrectAnswer === '' && newCorrectAnswer !== 0) {
      showToast('Please select/enter the correct answer', 'error');
      return;
    }

    try {
      await quizAPI.updateQuestionCorrectAnswer(viewingQuestions._id, questionId, newCorrectAnswer);
      showToast('Correct answer updated successfully', 'success');
      
      // Update local state
      const updatedQuestions = [...viewingQuestions.questions];
      updatedQuestions[questionIndex].correctAnswer = newCorrectAnswer;
      setViewingQuestions({ ...viewingQuestions, questions: updatedQuestions });
      
      setEditingAnswer(null);
      setNewCorrectAnswer('');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update correct answer', 'error');
    }
  };

  const startEditingAnswer = (question) => {
    setEditingAnswer(question._id);
    setNewCorrectAnswer(question.correctAnswer);
  };

  const cancelEditingAnswer = () => {
    setEditingAnswer(null);
    setNewCorrectAnswer('');
  };

  const handleUpdateQuestionText = async (question) => {
    if (!newQuestionText.trim()) {
      showToast('Question text cannot be empty', 'error');
      return;
    }

    try {
      await quizAPI.updateQuestionText(viewingQuestions._id, question._id, newQuestionText);
      showToast('Question updated successfully', 'success');
      
      // Update local state
      const updatedQuestions = viewingQuestions.questions.map(q => 
        q._id === question._id ? { ...q, question: newQuestionText } : q
      );
      setViewingQuestions({ ...viewingQuestions, questions: updatedQuestions });
      
      setEditingQuestion(null);
      setNewQuestionText('');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update question', 'error');
    }
  };

  const startEditingQuestion = (question) => {
    setEditingQuestion(question._id);
    setNewQuestionText(question.question);
  };

  const cancelEditingQuestion = () => {
    setEditingQuestion(null);
    setNewQuestionText('');
  };

  const handleUpdateOptionText = async (questionId, questionIndex, optionIndex) => {
    if (!newOptionText.trim()) {
      showToast('Option text cannot be empty', 'error');
      return;
    }

    try {
      await quizAPI.updateQuestionOption(viewingQuestions._id, questionId, optionIndex, newOptionText);
      showToast('Option updated successfully', 'success');
      
      // Update local state
      const updatedQuestions = [...viewingQuestions.questions];
      const newOptions = [...updatedQuestions[questionIndex].options];
      newOptions[optionIndex] = newOptionText;
      updatedQuestions[questionIndex].options = newOptions;
      setViewingQuestions({ ...viewingQuestions, questions: updatedQuestions });
      
      setEditingOption(null);
      setNewOptionText('');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update option', 'error');
    }
  };

  const startEditingOption = (questionId, optionIndex, optionText) => {
    setEditingOption({ questionId, optionIndex });
    setNewOptionText(optionText);
  };

  const cancelEditingOption = () => {
    setEditingOption(null);
    setNewOptionText('');
  };

  const handleDeleteQuestion = async (quizId, questionIndex) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    try {
      await quizAPI.deleteQuestion(quizId, questionIndex);
      showToast('Question deleted successfully', 'success');
      
      // Update the viewingQuestions state immediately by removing the question
      if (viewingQuestions) {
        const updatedQuestions = viewingQuestions.questions.filter((_, idx) => idx !== questionIndex);
        
        if (updatedQuestions.length > 0) {
          setViewingQuestions({ ...viewingQuestions, questions: updatedQuestions });
        } else {
          // If no questions left, close modal
          setViewingQuestions(null);
        }
      }
      
      // Refresh the quiz list in the background
      const filters = { search: searchQuery, group: selectedGroup, tags: selectedTags, startDate, endDate };
      fetchQuizzes(currentPage, filters);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete question';
      showToast(errorMsg, 'error');
    }
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);
  };

  const handleUpdateQuiz = async (quizId, updatedData) => {
    try {
      await quizAPI.updateQuiz(quizId, updatedData);
      showToast('Quiz updated successfully', 'success');
      setEditingQuiz(null);
      const filters = { search: searchQuery, group: selectedGroup, tags: selectedTags, startDate, endDate };
      fetchQuizzes(currentPage, filters);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update quiz';
      showToast(errorMsg, 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (viewingFlaggedQuestions) {
    return <FlaggedQuestions onBack={() => setViewingFlaggedQuestions(false)} />;
  }

  if (viewingIssues) {
    return <IssuesManagement onBack={() => setViewingIssues(false)} />;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>Quizzes</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>Manage your quizzes and view submissions</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => setViewingIssues(true)}
              style={{ fontSize: '13px' }}
            >
              🐛 Question Issues
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setViewingFlaggedQuestions(true)}
              style={{ fontSize: '13px' }}
            >
              🚩 Flagged Questions
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? '✕ Cancel' : '+ New Quiz'}
            </button>
          </div>
        </div>

        {showCreateForm && (
          <CreateQuiz onQuizCreated={handleQuizCreated} />
        )}

        <div style={{ marginTop: '32px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2d3748', margin: 0 }}>Your Quizzes ({quizzes.length} of {pagination.totalQuizzes})</h3>
          </div>
          
          <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '12px' }}>🔍 Filter Quizzes</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              {/* Search by title */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setSearchQuery(searchTerm);
                      setSearchParams({ page: '1' });
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              {/* Group filter */}
              <select
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSearchParams({ page: '1' });
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
              >
                <option value="">All Groups</option>
                {allGroups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </select>

              {/* Tags filter - Multi-select */}
              <div style={{ position: 'relative' }}>
                <div
                  data-tags-trigger="true"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minHeight: '37px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px'
                  }}
                  onClick={() => {
                    const dropdown = document.getElementById('tags-dropdown');
                    if (dropdown) {
                      dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                    }
                  }}
                >
                  {selectedTags.length === 0 ? (
                    <span style={{ color: '#a0aec0' }}>All Tags</span>
                  ) : (
                    selectedTags.map(tagId => {
                      const tag = allTags.find(t => t._id === tagId);
                      return tag ? (
                        <span
                          key={tagId}
                          style={{
                            backgroundColor: '#eef2ff',
                            color: '#667eea',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          {tag.name}
                        </span>
                      ) : null;
                    })
                  )}
                </div>
                <div
                  id="tags-dropdown"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  {allTags.map(tag => (
                    <label
                      key={tag._id}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTags([...selectedTags, tag._id]);
                          } else {
                            setSelectedTags(selectedTags.filter(id => id !== tag._id));
                          }
                          setSearchParams({ page: '1' });
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      {tag.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Start date */}
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setSearchParams({ page: '1' });
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
                placeholder="Start date"
              />

              {/* End date */}
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setSearchParams({ page: '1' });
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
                placeholder="End date"
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setSearchQuery(searchTerm);
                  setSearchParams({ page: '1' });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Apply Filters
              </button>
              {(searchQuery || selectedGroup || selectedTags.length > 0 || startDate || endDate) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchQuery('');
                    setSelectedGroup('');
                    setSelectedTags([]);
                    setStartDate('');
                    setEndDate('');
                    setSearchParams({ page: '1' });
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f7fafc',
                    color: '#4a5568',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {fetching ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#667eea', fontSize: '14px' }}>
            <div style={{ marginBottom: '12px' }}>Loading quizzes...</div>
          </div>
        ) : quizzes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#718096', fontSize: '14px' }}>
            {(searchQuery || selectedGroup || selectedTags.length > 0 || startDate || endDate) ? (
              <>
                <p style={{ marginBottom: '8px', fontSize: '15px' }}>No quizzes found matching your filters</p>
                <p style={{ fontSize: '13px' }}>Try adjusting your filters to see more results</p>
              </>
            ) : (
              <>
                <p style={{ marginBottom: '8px', fontSize: '15px' }}>No quizzes created yet</p>
                <p style={{ fontSize: '13px' }}>Create your first quiz to get started!</p>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {quizzes.map((quiz) => (
              <div 
                key={quiz._id} 
                onClick={() => navigate(`/admin/quiz/${quiz._id}`, { state: { quiz } })}
                style={{ 
                  border: '1.5px solid #e2e8f0', 
                  borderRadius: '12px', 
                  padding: '16px',
                  transition: 'all 0.2s ease',
                  background: '#fafafa',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header: Title and Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 4px 0', color: '#667eea', fontSize: '16px', fontWeight: '600' }}>
                      {quiz.title}
                    </h4>
                    {quiz.description && (
                      <p style={{ color: '#718096', margin: 0, fontSize: '13px', lineHeight: '1.4' }}>
                        {quiz.description.length > 100 ? quiz.description.substring(0, 100) + '...' : quiz.description}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditQuiz(quiz);
                      }}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: '#f7fafc',
                        color: '#667eea',
                        border: '1.5px solid #667eea',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#667eea';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.color = '#667eea';
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewingQuestions(quiz);
                      }}
                      style={{
                        padding: '5px 12px',
                        backgroundColor: '#f7fafc',
                        color: '#667eea',
                        border: '1.5px solid #667eea',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#667eea';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.color = '#667eea';
                      }}
                    >
                      👁️
                    </button>
                  </div>
                </div>

                {/* Metadata: Code, Group, Type, Difficulty in one row */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#667eea', padding: '3px 8px', background: '#eef2ff', borderRadius: '5px', fontSize: '12px' }}>
                    {quiz.quizCode}
                  </span>
                  {quiz.group && (
                    <span 
                      style={{ fontWeight: '600', color: '#38a169', padding: '3px 8px', background: '#c6f6d5', borderRadius: '5px', fontSize: '11px', cursor: 'help', position: 'relative' }}
                      title={quiz.group.students && quiz.group.students.length > 0 
                        ? `Students (${quiz.group.students.length}): ${quiz.group.students.map(s => s.name).join(', ')}`
                        : `Group: ${quiz.group.name}`
                      }
                    >
                      👥 {quiz.group.name} {quiz.group.students && quiz.group.students.length > 0 && `(${quiz.group.students.length})`}
                    </span>
                  )}
                  {quiz.difficulty && (
                    <span style={{ 
                      padding: '3px 8px', 
                      background: quiz.difficulty === 'Easy' ? '#c6f6d5' : quiz.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
                      color: quiz.difficulty === 'Easy' ? '#22543d' : quiz.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
                      borderRadius: '5px',
                      fontWeight: '600',
                      fontSize: '11px'
                    }}>
                      {quiz.difficulty === 'Easy' ? '🟢' : quiz.difficulty === 'Medium' ? '🟡' : '🔴'} {quiz.difficulty}
                    </span>
                  )}
                  <span style={{ color: '#718096', fontSize: '12px' }}>📝 {quiz.questions.length}</span>
                  <span style={{ color: '#718096', fontSize: '12px' }}>⏱️ {quiz.duration}m</span>
                  {quiz.randomizeQuestions && (
                    <span style={{ 
                      padding: '3px 8px', 
                      backgroundColor: '#e6fffa',
                      color: '#234e52',
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      🔀 Randomized
                    </span>
                  )}
                  {quiz.showResultsToStudents === false && (
                    <span style={{ 
                      padding: '3px 8px', 
                      backgroundColor: '#fef3c7',
                      color: '#78350f',
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      🔒 Results Hidden
                    </span>
                  )}
                  <span style={{ 
                    padding: '3px 8px', 
                    backgroundColor: quiz.submissionCount > 0 ? '#c6f6d5' : '#fed7d7',
                    color: quiz.submissionCount > 0 ? '#2f855a' : '#c53030',
                    borderRadius: '5px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    👥 {quiz.submissionCount || 0}
                  </span>
                </div>

                {/* Tags - Only show if present */}
                {quiz.tags && quiz.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {quiz.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '2px 8px',
                          background: 'white',
                          color: '#667eea',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #c7d2fe'
                        }}
                      >
                        {tag.name || tag}
                      </span>
                    ))}
                    {quiz.tags.length > 3 && (
                      <span style={{ fontSize: '10px', color: '#a0aec0' }}>+{quiz.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div style={{ 
            marginTop: '24px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: '8px',
            paddingTop: '24px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              style={{ 
                fontSize: '12px', 
                padding: '8px 16px',
                opacity: pagination.hasPrevPage ? 1 : 0.5,
                cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed'
              }}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[...Array(pagination.totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first page, last page, current page, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: currentPage === pageNum ? '2px solid #667eea' : '1px solid #e2e8f0',
                        backgroundColor: currentPage === pageNum ? '#eef2ff' : 'white',
                        color: currentPage === pageNum ? '#667eea' : '#4a5568',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        minWidth: '36px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.borderColor = '#667eea';
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === currentPage - 2 && currentPage > 3) ||
                  (pageNum === currentPage + 2 && currentPage < pagination.totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ color: '#718096', padding: '0 4px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              style={{ 
                fontSize: '12px', 
                padding: '8px 16px',
                opacity: pagination.hasNextPage ? 1 : 0.5,
                cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed'
              }}
            >
              Next →
            </button>
          </div>
        )}

        {/* Page Info */}
        {pagination.totalQuizzes > 0 && (
          <div style={{ 
            marginTop: '16px', 
            textAlign: 'center', 
            fontSize: '12px', 
            color: '#718096' 
          }}>
            Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.totalQuizzes)} of {pagination.totalQuizzes} quizzes
          </div>
        )}
      </div>

      {/* Questions Modal */}
      {viewingQuestions && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => {
          setViewingQuestions(null);
          setQuestionSearchTerm('');
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', color: '#667eea', fontSize: '20px', fontWeight: '600' }}>
                  📝 Quiz Questions
                </h3>
                <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
                  {viewingQuestions.title} - {viewingQuestions.questions.length} questions
                </p>
              </div>
              <button
                onClick={() => {
                  setViewingQuestions(null);
                  setQuestionSearchTerm('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="🔍 Search questions by title or option text..."
                  value={questionSearchTerm}
                  onChange={(e) => setQuestionSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    fontSize: '14px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '16px',
                  color: '#718096'
                }}>🔍</span>
                {questionSearchTerm && (
                  <button
                    onClick={() => setQuestionSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: 'transparent',
                      color: '#718096',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#2d3748'}
                    onMouseLeave={(e) => e.target.style.color = '#718096'}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
              {questionSearchTerm && (
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: '12px', 
                  color: '#718096',
                  fontWeight: '500'
                }}>
                  Showing {viewingQuestions.questions.filter((question) => {
                    const searchLower = questionSearchTerm.toLowerCase();
                    const questionTextMatches = question.question.toLowerCase().includes(searchLower);
                    const optionMatches = question.options.some(option => 
                      option.toLowerCase().includes(searchLower)
                    );
                    return questionTextMatches || optionMatches;
                  }).length} of {viewingQuestions.questions.length} questions
                </p>
              )}
            </div>

            {/* Questions List Header */}
            <div style={{
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h4 style={{ 
                margin: 0, 
                color: '#2d3748', 
                fontSize: '16px', 
                fontWeight: '700' 
              }}>
                📋 Questions List
              </h4>
              {!questionSearchTerm && (
                <span style={{ 
                  fontSize: '13px', 
                  color: '#718096',
                  fontWeight: '500'
                }}>
                  {viewingQuestions.questions.length} total
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              {(() => {
                // Filter questions based on search term
                const filteredQuestions = viewingQuestions.questions.filter((question) => {
                  if (!questionSearchTerm) return true;
                  
                  const searchLower = questionSearchTerm.toLowerCase();
                  
                  // Check if question text matches
                  const questionTextMatches = question.question.toLowerCase().includes(searchLower);
                  
                  // Check if any option matches
                  const optionMatches = question.options.some(option => 
                    option.toLowerCase().includes(searchLower)
                  );
                  
                  return questionTextMatches || optionMatches;
                });
                
                if (filteredQuestions.length === 0) {
                  return (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      backgroundColor: '#f7fafc',
                      borderRadius: '10px',
                      border: '2px dashed #e2e8f0'
                    }}>
                      <p style={{ margin: 0, color: '#718096', fontSize: '15px' }}>
                        🔍 No questions found matching "{questionSearchTerm}"
                      </p>
                      <button
                        onClick={() => setQuestionSearchTerm('')}
                        style={{
                          marginTop: '12px',
                          padding: '6px 16px',
                          fontSize: '13px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Clear Search
                      </button>
                    </div>
                  );
                }
                
                return filteredQuestions.map((question, index) => {
                  // Get the original index from the full questions array
                  const originalIndex = viewingQuestions.questions.findIndex(q => q._id === question._id);
                  
                  return (
                    <div key={question._id} style={{
                      backgroundColor: '#f7fafc',
                      borderRadius: '10px',
                      padding: '18px',
                      border: '1px solid #e2e8f0',
                      position: 'relative'
                    }}>
                      {/* Header with Question Number, Difficulty and Action Buttons */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '12px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <h4 style={{ margin: 0, color: '#2d3748', fontSize: '15px', fontWeight: '600' }}>
                            Question {originalIndex + 1}
                          </h4>
                          <ReportIssueButton
                            sourceType="quiz"
                            sourceId={viewingQuestions._id}
                            questionPath={`questions.${originalIndex}`}
                            questionId={question._id}
                            questionIndex={originalIndex}
                            questionData={{
                              question: question.question,
                              options: question.options,
                              correctAnswer: question.correctAnswer,
                              questionType: question.questionType,
                              explanation: question.explanation,
                              difficulty: question.difficulty,
                              tags: question.tags,
                              category: question.category
                            }}
                            metadata={{
                              sourceName: viewingQuestions.title,
                              sourceCode: viewingQuestions.code,
                              questionNumber: originalIndex + 1,
                              totalQuestions: viewingQuestions.questions?.length || 0,
                              reportedFrom: 'admin-question-view',
                              timestamp: new Date().toISOString(),
                              groupName: viewingQuestions.group?.name,
                              groupId: viewingQuestions.group?._id
                            }}
                            size="icon-only"
                          />
                          {question.difficulty && (
                        <span style={{
                          padding: '4px 10px',
                          background: question.difficulty === 'Easy' ? '#c6f6d5' : question.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
                          color: question.difficulty === 'Easy' ? '#22543d' : question.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '11px'
                        }}>
                          {question.difficulty === 'Easy' ? '🟢' : question.difficulty === 'Medium' ? '🟡' : '🔴'} {question.difficulty}
                        </span>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button
                        onClick={() => startEditingQuestion(question)}
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          backgroundColor: '#805ad5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6b46c1'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#805ad5'}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => startEditingAnswer(question)}
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a67d8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
                      >
                        ✓ Answer
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(viewingQuestions._id, originalIndex)}
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          backgroundColor: '#fff5f5',
                          color: '#c53030',
                          border: '1.5px solid #fc8181',
                          borderRadius: '5px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#c53030';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff5f5';
                          e.currentTarget.style.color = '#c53030';
                        }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>

                      {/* Question Text */}
                      <div style={{ marginBottom: '12px' }}>
                        {editingQuestion === question._id ? (
                          <div>
                            <textarea
                              value={newQuestionText}
                              onChange={(e) => setNewQuestionText(e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '2px solid #3182ce',
                                fontSize: '14px',
                                outline: 'none',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                              }}
                              placeholder="Enter question text"
                            />
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleUpdateQuestionText(question)}
                                style={{
                                  padding: '6px 16px',
                                  fontSize: '13px',
                                  backgroundColor: '#48bb78',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                ✓ Save
                              </button>
                              <button
                                onClick={cancelEditingQuestion}
                                style={{
                                  padding: '6px 16px',
                                  fontSize: '13px',
                                  backgroundColor: '#cbd5e0',
                                  color: '#2d3748',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p style={{ margin: 0, color: '#4a5568', fontSize: '14px', lineHeight: '1.6' }}>
                            <FormattedText text={question.question} />
                          </p>
                        )}
                      </div>

                      {/* Code Reference Section */}
                      {viewingQuestions.codeReference && viewingQuestions.codeReference.showToStudents && viewingQuestions.codeReference.code && (
                        <div style={{ 
                          marginBottom: '16px',
                          padding: '16px',
                          backgroundColor: '#f7fafc',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #e2e8f0'
                          }}>
                            <span style={{ fontSize: '16px', marginRight: '8px' }}>💻</span>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                              Code Reference ({viewingQuestions.codeReference.language})
                            </h4>
                          </div>
                          <SyntaxHighlighter
                            language={viewingQuestions.codeReference.language.toLowerCase()}
                            style={vscDarkPlus}
                            customStyle={{
                              borderRadius: '6px',
                              fontSize: '13px',
                              margin: 0,
                              padding: '16px'
                            }}
                            showLineNumbers={true}
                          >
                            {viewingQuestions.codeReference.code}
                          </SyntaxHighlighter>
                        </div>
                      )}

                  {/* Options Section */
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', margin: 0 }}>Options</h5>
                          {editingAnswer === question._id && (
                            <div>
                              <button
                                onClick={() => handleUpdateCorrectAnswer(question._id, originalIndex)}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '12px',
                                  backgroundColor: '#48bb78',
                                  color: 'white',
                                  border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '4px'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditingAnswer}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: '#cbd5e0',
                              color: '#2d3748',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {question.options.map((option, optIndex) => {
                            const isEditingThisOption = editingOption?.questionId === question._id && editingOption?.optionIndex === optIndex;
                            
                            return (
                              <div key={optIndex}>
                                {isEditingThisOption ? (
                                  <div style={{
                                padding: '10px 12px',
                                borderRadius: '6px',
                                backgroundColor: '#eef2ff',
                                border: '2px solid #667eea'
                              }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <span style={{ fontWeight: '700', color: '#4a5568' }}>
                                        {String.fromCharCode(65 + optIndex)}.
                                      </span>
                                      <input
                                    type="text"
                                    value={newOptionText}
                                    onChange={(e) => setNewOptionText(e.target.value)}
                                    style={{
                                      flex: 1,
                                      padding: '6px 10px',
                                      fontSize: '13px',
                                      border: '1px solid #cbd5e0',
                                      borderRadius: '4px',
                                      outline: 'none'
                                    }}
                                    placeholder="Enter option text"
                                        autoFocus
                                      />
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                      <button
                                    onClick={() => handleUpdateOptionText(question._id, originalIndex, optIndex)}
                                    style={{
                                      padding: '4px 12px',
                                      fontSize: '11px',
                                      backgroundColor: '#48bb78',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓ Save
                                  </button>
                                  <button
                                    onClick={cancelEditingOption}
                                    style={{
                                      padding: '4px 12px',
                                      fontSize: '11px',
                                      backgroundColor: '#cbd5e0',
                                      color: '#2d3748',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                onClick={() => editingAnswer === question._id && setNewCorrectAnswer(optIndex)}
                                style={{
                                  padding: '10px 12px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  backgroundColor: editingAnswer === question._id && newCorrectAnswer === optIndex ? '#bee3f8' :
                                                   optIndex === question.correctAnswer ? '#c6f6d5' : 'white',
                                  border: editingAnswer === question._id && newCorrectAnswer === optIndex ? '2px solid #3182ce' :
                                          optIndex === question.correctAnswer ? '1.5px solid #48bb78' : '1px solid #e2e8f0',
                                  color: optIndex === question.correctAnswer ? '#2f855a' : '#4a5568',
                                  fontWeight: optIndex === question.correctAnswer ? '600' : '500',
                                  cursor: editingAnswer === question._id ? 'pointer' : 'default',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                    <div style={{ flex: 1 }}>
                                      <span style={{ fontWeight: '700', marginRight: '6px' }}>
                                        {String.fromCharCode(65 + optIndex)}.
                                      </span>
                                      <FormattedText text={option} />
                                      {editingAnswer === question._id && newCorrectAnswer === optIndex && <span style={{ marginLeft: '8px', color: '#3182ce' }}>← Select this</span>}
                                      {editingAnswer !== question._id && optIndex === question.correctAnswer && (
                                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>✓ Correct Answer</span>
                                      )}
                                    </div>
                                    {editingAnswer !== question._id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEditingOption(question._id, optIndex, option);
                                    }}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: '11px',
                                      backgroundColor: 'transparent',
                                      color: '#667eea',
                                      border: '1px solid #667eea',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                      transition: 'all 0.2s',
                                      marginLeft: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#667eea';
                                      e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#667eea';
                                    }}
                                  >
                                        ✏️
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      }
                      {question.explanation && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          backgroundColor: '#fefcbf',
                          borderLeft: '3px solid #d69e2e',
                          borderRadius: '6px'
                        }}>
                          <p style={{
                            fontWeight: '600',
                            marginBottom: '4px',
                            color: '#744210',
                            fontSize: '12px'
                          }}>
                            💡 Explanation
                          </p>
                          <p style={{ margin: 0, color: '#744210', lineHeight: '1.6', fontSize: '13px' }}>
                            {question.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Edit Quiz Modal */}
      {editingQuiz && (
        <EditQuizModal
          quiz={editingQuiz}
          onClose={() => setEditingQuiz(null)}
          onUpdate={handleUpdateQuiz}
        />
      )}
    </div>
  );
}

// Edit Quiz Modal Component
function EditQuizModal({ quiz, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    title: quiz.title,
    description: quiz.description || '',
    duration: quiz.duration,
    difficulty: quiz.difficulty,
    randomizeQuestions: quiz.randomizeQuestions || false,
    showResultsToStudents: quiz.showResultsToStudents !== false, // default to true if not set
    startDate: quiz.startDate ? new Date(quiz.startDate).toISOString().slice(0, 16) : '',
    endDate: quiz.endDate ? new Date(quiz.endDate).toISOString().slice(0, 16) : '',
    group: quiz.group?._id || quiz.group || '',
  });
  const [availableGroups, setAvailableGroups] = useState([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await groupAPI.getMyGroups();
        setAvailableGroups(response.data.groups || []);
      } catch (err) {
        console.error('Error fetching groups:', err);
      }
    };
    fetchGroups();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(quiz._id, formData);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#667eea', fontSize: '20px', fontWeight: '600' }}>
            ✏️ Edit Quiz
          </h3>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f7fafc',
              color: '#4a5568',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ✕ Close
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Quiz Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter quiz title"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Enter quiz description"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Duration (minutes) *</label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                min="5"
                max="180"
                required
              />
            </div>

            <div className="form-group">
              <label>Difficulty Level *</label>
              <select
                name="difficulty"
                value={formData.difficulty}
                onChange={handleChange}
                required
              >
                <option value="Easy">🟢 Easy</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Hard">🔴 Hard</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                name="randomizeQuestions"
                checked={formData.randomizeQuestions}
                onChange={handleChange}
                style={{ 
                  marginRight: '10px', 
                  width: '18px', 
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: '600', color: '#2d3748' }}>
                🔀 Randomize Question Order
              </span>
            </label>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                name="showResultsToStudents"
                checked={formData.showResultsToStudents}
                onChange={handleChange}
                style={{ 
                  marginRight: '10px', 
                  width: '18px', 
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: '600', color: '#2d3748' }}>
                👁️ Show Results to Students
              </span>
            </label>
          </div>

          <div className="form-group">
            <label style={{ marginBottom: '8px', display: 'block', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
              👥 Group (Optional)
            </label>
            <select
              name="group"
              value={formData.group}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#2d3748',
                backgroundColor: 'white'
              }}
            >
              <option value="">No group (available to all students)</option>
              {availableGroups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.students.length} students)
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Start Date & Time</label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>End Date & Time</label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button 
              type="submit" 
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              💾 Save Changes
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminQuizzes;
